import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, gbp, formatApiError } from "@/lib/api";

const EMPTY = { name: "", duration: 60, price: 50, description: "", active: true };

const ServiceDialog = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(initial || EMPTY); }, [initial, open]);
  const save = async () => {
    try {
      const body = { ...f, duration: Number(f.duration), price: Number(f.price) };
      if (initial?.id) await api.put(`/admin/services/${initial.id}`, body); else await api.post("/admin/services", body);
      toast.success("Treatment saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="service-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit treatment" : "New treatment"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="service-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" min={5} value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} data-testid="service-duration-input" /></div>
            <div className="space-y-1.5"><Label>Price (£)</Label><Input type="number" min={0} step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="service-price-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="service-description-input" /></div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="service-active-switch" />Bookable online</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name} data-testid="service-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Services() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/services").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/admin/services/${s.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fade-up">
      <PageHead eyebrow="Catalogue" title="Treatments">
        <Button className="rounded-full" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-service-button"><Plus className="h-4 w-4 mr-1.5" />Add treatment</Button>
      </PageHead>
      <div className="rounded-2xl bg-card border border-border divide-y divide-border">
        {list.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-4 p-5" data-testid={`admin-service-row-${s.id}`}>
            <div>
              <p className="font-display font-bold">{s.name} {!s.active && <span className="ml-2 text-xs text-muted-foreground font-normal">(hidden)</span>}</p>
              <p className="text-sm text-muted-foreground">{s.duration} min · {s.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold mr-2">{gbp(s.price)}</span>
              <Button variant="ghost" size="icon" onClick={() => { setEdit(s); setOpen(true); }} data-testid={`edit-service-button-${s.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-service-button-${s.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="p-6 text-muted-foreground">No treatments yet.</p>}
      </div>
      <ServiceDialog open={open} onClose={() => setOpen(false)} initial={edit} onSaved={load} />
    </div>
  );
}
